import type { AgentEvent } from "@rca/sdk";
import type { RunStatus } from "@rca/sdk";

/**
 * 実行イベント列を、画面に出す形へ畳み込む純関数（W5-1）。
 *
 * 副作用を持たないので、フィクスチャを流すだけで表示の正しさを検証できる。
 * 未知のイベントは**無視するが数える**（取りこぼしに気づけるように）。
 */

export interface ChatMessage {
  readonly id: string;
  readonly kind: "prompt" | "assistant" | "thinking" | "tool" | "error" | "status";
  readonly text: string;
  readonly ts: string;
  /** ツール呼び出しの結果（対応する tool.result を受け取ると埋まる）。 */
  readonly tool?: {
    readonly name: string;
    readonly input: unknown;
    readonly status: "running" | "ok" | "failed";
    readonly output?: unknown;
  };
}

export interface ChatState {
  readonly runId: string | null;
  readonly status: RunStatus | "idle";
  readonly messages: readonly ChatMessage[];
  readonly usage: {
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
  } | null;
  readonly finalText: string | null;
  readonly lastSeq: number;
  /** 正規化できなかったイベントの総数（保持上限で切り捨てた分も数える）。 */
  readonly unknownEvents: number;
  /**
   * `runner.raw`（エージェント CLI の生出力）の直近ぶん。
   *
   * パーサが構造化できなかった行はここに来る。**捨てない**のが大事で、
   * 認証エラーやツールの拒否といった「なぜ動かないか」は
   * だいたいこちら側にしか出てこない。
   */
  readonly rawEvents: readonly RawEventEntry[];
  readonly error: { code: string; message: string } | null;
}

export interface RawEventEntry {
  readonly seq: number;
  readonly ts: string;
  readonly note: string | undefined;
  readonly raw: unknown;
}

/** 長い Run でメモリを食い潰さないための保持上限。直近を残す。 */
export const RAW_EVENT_LIMIT = 200;

export const initialChatState: ChatState = {
  runId: null,
  status: "idle",
  messages: [],
  usage: null,
  finalText: null,
  lastSeq: 0,
  unknownEvents: 0,
  rawEvents: [],
  error: null,
};

export type ChatAction =
  | {
      type: "run-created";
      runId: string;
      prompt: string;
      ts: string;
      /**
       * 直前までの表示を残すか。
       *
       * セッション継続では**前のやり取りが見えていないと会話にならない**。
       * 単発実行では毎回作り直したいので、既定は false のまま。
       */
      keepHistory?: boolean;
    }
  /**
   * 送信した内容を、応答を待たずに出す。
   *
   * detached では Run の作成にコンテナの確保が入り、30 秒前後かかる。
   * 応答を待ってから描くと、その間ユーザーの入力が消えたように見える。
   */
  | { type: "prompt-sent"; prompt: string; ts: string; keepHistory?: boolean }
  | { type: "agent-event"; event: AgentEvent }
  /**
   * 表示中の Run の状態を外から教える。
   *
   * 履歴を読み直したときに使う。`run-created` は "queued" から始めるので、
   * 終わった Run を並べたあとに直さないと**実行中扱いのまま入力欄が塞がる**。
   */
  | { type: "run-status-known"; status: RunStatus }
  | { type: "reset" };

interface StatusData {
  from: RunStatus | null;
  to: RunStatus;
  reason?: string;
}
interface MessageData {
  role: string;
  text: string;
}
interface ToolCallData {
  toolCallId: string;
  name: string;
  input: unknown;
}
interface ToolResultData {
  toolCallId: string;
  ok: boolean;
  output: unknown;
}
interface UsageData {
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
}
interface ErrorData {
  code: string;
  message: string;
}
interface ResultData {
  status: RunStatus;
  finalText?: string;
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "reset":
      return initialChatState;

    case "prompt-sent": {
      const keep = action.keepHistory === true;
      const prompt: ChatMessage = {
        // Run が決まる前なので、id は時刻で作る。
        id: `prompt-pending-${action.ts}`,
        kind: "prompt",
        text: action.prompt,
        ts: action.ts,
      };
      return {
        ...(keep ? state : initialChatState),
        status: "queued",
        messages: keep ? [...state.messages, prompt] : [prompt],
      };
    }

    case "run-created": {
      const keep = action.keepHistory === true;
      const prompt: ChatMessage = {
        id: `prompt-${action.runId}`,
        kind: "prompt",
        text: action.prompt,
        ts: action.ts,
      };
      return {
        ...(keep ? state : initialChatState),
        runId: action.runId,
        status: "queued",
        // seq は Run ごとに 1 から振られる。継続時はここで戻さないと
        // 次の Run の先頭イベントが重複排除で捨てられる。
        lastSeq: 0,
        // 直前 Run のエラー・使用量・生ログは引きずらない。
        error: null,
        usage: null,
        unknownEvents: 0,
        rawEvents: [],
        finalText: null,
        // 送信時に出した仮の表示を、Run の id を持つものに差し替える。
        // 消してから足すと画面が一瞬ちらつくので、置き換えにする。
        messages: replacePending(keep ? state.messages : [], prompt),
      };
    }

    case "run-status-known":
      return { ...state, status: action.status };

    case "agent-event":
      return applyEvent(state, action.event);
  }
}

/**
 * イベント 1 件を状態に反映する。
 *
 * 表示要素の id には **必ず runId を含める**。`seq` は Run ごとに 1 から
 * 振られるので、セッション履歴のように複数 Run を並べると衝突する
 * （React のキー重複になり、要素が消えたり入れ替わったりする）。
 */
/** 送信時に出した仮の表示を、Run が決まったものへ差し替える。 */
function replacePending(messages: readonly ChatMessage[], prompt: ChatMessage): ChatMessage[] {
  const index = messages.findIndex((m) => m.id.startsWith("prompt-pending-"));
  if (index === -1) return [...messages, prompt];
  return [...messages.slice(0, index), prompt, ...messages.slice(index + 1)];
}

function applyEvent(state: ChatState, event: AgentEvent): ChatState {
  // 同じイベントを二度受け取っても状態が進まないようにする（再接続の保険）。
  if (event.seq <= state.lastSeq) return state;
  const base = { ...state, lastSeq: event.seq };

  switch (event.type) {
    case "run.status": {
      const data = event.data as StatusData;
      return {
        ...base,
        status: data.to,
        messages: [
          ...base.messages,
          {
            id: `status-${event.runId}-${event.seq}`,
            kind: "status",
            text: data.reason === undefined ? data.to : `${data.to}: ${data.reason}`,
            ts: event.ts,
          },
        ],
      };
    }

    case "agent.message": {
      const data = event.data as MessageData;
      return {
        ...base,
        messages: [
          ...base.messages,
          { id: `msg-${event.runId}-${event.seq}`, kind: "assistant", text: data.text, ts: event.ts },
        ],
      };
    }

    case "agent.thinking": {
      const data = event.data as { text: string };
      return {
        ...base,
        messages: [
          ...base.messages,
          { id: `think-${event.runId}-${event.seq}`, kind: "thinking", text: data.text, ts: event.ts },
        ],
      };
    }

    case "tool.call": {
      const data = event.data as ToolCallData;
      return {
        ...base,
        messages: [
          ...base.messages,
          {
            id: `tool-${event.runId}-${data.toolCallId}`,
            kind: "tool",
            text: data.name,
            ts: event.ts,
            tool: { name: data.name, input: data.input, status: "running" },
          },
        ],
      };
    }

    case "tool.result": {
      const data = event.data as ToolResultData;
      // 探すときも runId を含める（別 Run の同名 toolCallId を拾わない）。
      const index = base.messages.findIndex((m) => m.id === `tool-${event.runId}-${data.toolCallId}`);
      if (index === -1) {
        // 対応する呼び出しを見ていない（途中から購読した場合など）。
        return {
          ...base,
          messages: [
            ...base.messages,
            {
              id: `tool-${event.runId}-${data.toolCallId}`,
              kind: "tool",
              text: "(不明なツール)",
              ts: event.ts,
              tool: {
                name: "unknown",
                input: undefined,
                status: data.ok ? "ok" : "failed",
                output: data.output,
              },
            },
          ],
        };
      }
      const messages = [...base.messages];
      const target = messages[index]!;
      messages[index] = {
        ...target,
        tool: {
          name: target.tool?.name ?? "unknown",
          input: target.tool?.input,
          status: data.ok ? "ok" : "failed",
          output: data.output,
        },
      };
      return { ...base, messages };
    }

    case "agent.usage": {
      const data = event.data as UsageData;
      const previous = base.usage ?? { inputTokens: 0, outputTokens: 0, costUsd: 0 };
      return {
        ...base,
        usage: {
          inputTokens: previous.inputTokens + (data.inputTokens ?? 0),
          outputTokens: previous.outputTokens + (data.outputTokens ?? 0),
          costUsd: previous.costUsd + (data.costUsd ?? 0),
        },
      };
    }

    case "run.error": {
      const data = event.data as ErrorData;
      return {
        ...base,
        error: { code: data.code, message: data.message },
        messages: [
          ...base.messages,
          {
            id: `error-${event.runId}-${event.seq}`,
            kind: "error",
            text: `${data.code}: ${data.message}`,
            ts: event.ts,
          },
        ],
      };
    }

    case "run.result": {
      const data = event.data as ResultData;
      return {
        ...base,
        status: data.status,
        finalText: data.finalText ?? base.finalText,
      };
    }

    default: {
      // `runner.raw` を含む未知イベント。会話としては表示しないが、
      // 中身は残して UI から開けるようにする（原因究明はここに出る）。
      const note = (event.data as { note?: unknown } | undefined)?.note;
      const entry: RawEventEntry = {
        seq: event.seq,
        ts: event.ts,
        note: typeof note === "string" ? note : undefined,
        raw: event.raw ?? event.data,
      };
      return {
        ...base,
        unknownEvents: base.unknownEvents + 1,
        rawEvents: [...base.rawEvents, entry].slice(-RAW_EVENT_LIMIT),
      };
    }
  }
}

/** 表示用に「思考」を折り畳むかどうかの既定。 */
export const isCollapsedByDefault = (message: ChatMessage): boolean =>
  message.kind === "thinking" || message.kind === "status";
